import { useEffect, useState } from "react";
import { Card, Button, Alert, Typography, Space, Badge } from "antd";
import {
  MdBlock,
  MdCheckCircle,
  MdWarning,
  MdShield,
  MdVerifiedUser,
  MdSettings,
  MdInfo,
} from "react-icons/md";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { FiShield } from "react-icons/fi";
import { setToken } from "../../config/index";
import { useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { useSetUserDetails } from "./store/store";

const { Title, Paragraph } = Typography;

const allowedOrigins = [
  "https://offline.dev.hav-g.in",
  "https://offline.traya.health",
];

const OfflineIframeLogin = () => {
  const [accessDenied, setAccessDenied] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const setUserDetails = useSetUserDetails((state) => state.setUserDetails);
  const location = useLocation();

  useEffect(() => {
    const validateAccess = async () => {
      const isEmbedded = window.self !== window.top;
      const searchParams = new URLSearchParams(location.search);
      const offlineToken = searchParams.get("token");
      const redirectPath = searchParams.get("redirect") || "/";
      const existingToken = Cookies.get("token");

      try {
        if (!isEmbedded) {
          throw new Error("This page must be embedded in an iframe");
        }

        const referrer = document.referrer;
        if (!referrer) throw new Error("Missing referrer.");
        const referrerOrigin = new URL(referrer).origin;

        if (!allowedOrigins.includes(referrerOrigin)) {
          throw new Error("Access denied: Untrusted embedding origin.");
        }

        if (existingToken) {
          window.location.href = redirectPath;
          return;
        }

        if (offlineToken) {
          Cookies.set("token", offlineToken);
          setToken(offlineToken);
          const currentUrl = new URL(window.location.href);
          currentUrl.searchParams.delete("token");
          window.history.replaceState(
            {},
            document.title,
            currentUrl.toString()
          );

          await setUserDetails?.();

          window.location.href = redirectPath;
          return;
        }

        throw new Error("Missing token");
      } catch (error) {
        console.warn("Access denied:", error.message);
        setAccessDenied(true);
      } finally {
        setIsLoading(false);
      }
    };

    const timer = setTimeout(validateAccess, 100);
    return () => clearTimeout(timer);
  }, [location, setUserDetails]);

  const cardStyle = {
    borderRadius: "16px",
    border: "none",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
    overflow: "hidden",
  };

  const iconStyle = {
    fontSize: "64px",
    marginBottom: "24px",
    display: "block",
  };

  // Add keyframes for spinning animation
  const spinKeyframes = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;

  // Inject the keyframes into the document head
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = spinKeyframes;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  if (isLoading) {
    return (
      <div
        className="min-vh-100 d-flex align-items-center justify-content-center"
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          padding: "20px",
        }}
      >
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-md-6 col-lg-4">
              <Card
                style={cardStyle}
                bodyStyle={{ padding: "48px 32px", textAlign: "center" }}
                className="animate__animated animate__fadeIn"
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginBottom: "24px",
                  }}
                >
                  <AiOutlineLoading3Quarters
                    style={{
                      ...iconStyle,
                      color: "#1890ff",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                </div>
                <Title
                  level={3}
                  style={{ marginBottom: "16px", color: "#262626" }}
                >
                  Verifying Access
                </Title>
                <Paragraph
                  style={{
                    color: "#8c8c8c",
                    marginBottom: "0",
                    fontSize: "16px",
                  }}
                >
                  Checking authorization and validating security protocols...
                </Paragraph>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div
        className="min-vh-100 d-flex align-items-center justify-content-center"
        style={{
          background: "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
          padding: "20px",
        }}
      >
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-md-8 col-lg-6">
              <Card
                style={cardStyle}
                bodyStyle={{ padding: "48px 32px", textAlign: "center" }}
                className="animate__animated animate__fadeIn"
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginBottom: "24px",
                  }}
                >
                  <MdBlock style={{ ...iconStyle, color: "#ff4d4f" }} />{" "}
                </div>

                <Title
                  level={2}
                  style={{ marginBottom: "16px", color: "#262626" }}
                >
                  Access Denied
                </Title>
                <Paragraph
                  style={{
                    color: "#595959",
                    marginBottom: "24px",
                    fontSize: "16px",
                  }}
                >
                  This content can only be embedded from an authorized host.
                  Please contact your system administrator if you believe this
                  is an error.
                </Paragraph>

                <Alert
                  type="error"
                  icon={<MdWarning />}
                  message="Security Notice"
                  description="Unauthorized embedding attempts are logged for security monitoring and compliance purposes."
                  style={{
                    marginBottom: "32px",
                    borderRadius: "8px",
                    textAlign: "left",
                  }}
                  showIcon
                />

                <div className="d-flex justify-content-center mb-3">
                  <Badge
                    color="#ff4d4f"
                    text={
                      <span style={{ color: "#ff4d4f", fontWeight: "500" }}>
                        🔒 Access Blocked
                      </span>
                    }
                  />
                </div>

                <Space
                  direction="vertical"
                  size="middle"
                  style={{ width: "100%" }}
                >
                  <Button
                    type="default"
                    size="large"
                    block
                    icon={<MdInfo />}
                    style={{ height: "48px", borderRadius: "8px" }}
                  >
                    Contact Support
                  </Button>
                  <Button type="text" size="large" style={{ color: "#8c8c8c" }}>
                    View Security Policy
                  </Button>
                </Space>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-vh-100 d-flex align-items-center justify-content-center"
      style={{
        background: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
        padding: "20px",
      }}
    >
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6">
            <Card
              style={cardStyle}
              bodyStyle={{ padding: "48px 32px", textAlign: "center" }}
              className="animate__animated animate__fadeIn"
            >
              <div style={{ textAlign: "center", marginBottom: "24px" }}>
                <MdShield style={{ fontSize: "64px", color: "#52c41a" }} />
              </div>
              <Title
                level={2}
                style={{ marginBottom: "16px", color: "#262626" }}
              >
                Secure Connection Verified
              </Title>
              <Paragraph
                style={{
                  color: "#595959",
                  marginBottom: "24px",
                  fontSize: "16px",
                }}
              >
                This content is being served inside an approved iframe from an
                authorized origin. Your connection is secure and authenticated.
              </Paragraph>

              <Alert
                type="success"
                icon={<MdCheckCircle />}
                message="Security Status: Active"
                description="Origin validation successful. All security protocols have been verified and connection is encrypted."
                style={{
                  marginBottom: "32px",
                  borderRadius: "8px",
                  textAlign: "left",
                }}
                showIcon
              />

              <div className="d-flex justify-content-center mb-4">
                <Badge
                  color="#52c41a"
                  text={
                    <span style={{ color: "#52c41a", fontWeight: "500" }}>
                      ✅ Secure & Authorized
                    </span>
                  }
                />
              </div>

              <Space
                direction="vertical"
                size="middle"
                style={{ width: "100%" }}
              >
                <Button
                  type="primary"
                  size="large"
                  block
                  icon={<MdVerifiedUser />}
                  style={{
                    height: "48px",
                    borderRadius: "8px",
                    background:
                      "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)",
                    border: "none",
                    fontWeight: "500",
                  }}
                >
                  Continue to TrayaConnect
                </Button>
                <Button
                  type="default"
                  size="large"
                  block
                  icon={<FiShield />}
                  style={{
                    height: "48px",
                    borderRadius: "8px",
                    borderColor: "#d9d9d9",
                  }}
                >
                  View Connection Details
                </Button>
                <Button
                  type="text"
                  size="large"
                  icon={<MdSettings />}
                  style={{ color: "#8c8c8c" }}
                >
                  Security & Privacy Settings
                </Button>
              </Space>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfflineIframeLogin;
